import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ShieldCheck,
  FileCheck,
  ArrowRight,
  Server,
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  RefreshCw,
  Trash2,
  Eye,
  BarChart2,
  Brain
} from 'lucide-react';
import GenerateReportModal from '../../components/Newdashboard/GenerateReportModal';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import { ModernButton, ModernCard, ModernBadge, ModernStatCard } from '../../components/ModernUI';
import './ComplianceReport.css';
import { useSelector } from "react-redux";

const ComplianceReport = () => {
  const navigate = useNavigate();
  const [availableScans, setAvailableScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, scanId: null, scanName: null });
  const [deleting, setDeleting] = useState(false);
  const [systemStats, setSystemStats] = useState({
    activeFrameworks: 0,
    totalControls: 0
  });

  const user = useSelector((state) => state?.users.users)

  useEffect(() => {
    fetchAvailableScans();
  }, []);

  const fetchAvailableScans = async () => {
    try {
      setLoading(true);
      let appendOrgId = "";
      if (user.orgId) {
        appendOrgId = `/${user.orgId}`
      }
      const response = await fetch(ApiNames.SCAN_REPORTS + appendOrgId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const scans = data.scan_reports || [];
      setAvailableScans(scans);

      if (scans.length > 0) {
        const latestScan = scans[0];
        setSystemStats({
          activeFrameworks: latestScan.frameworks_count || 0,
          totalControls: latestScan.total_controls || 0
        });
      } else {
        setSystemStats({
          activeFrameworks: 0,
          totalControls: 0
        });
      }
    } catch (error) {
      console.error('Error fetching scans:', error);
      toast.error('Failed to fetch available scans. Please ensure the API server is running.');
      setAvailableScans([]);
      setSystemStats({
        activeFrameworks: 0,
        totalControls: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAvailableScans();
    setRefreshing(false);
    toast.success('Scan list refreshed!');
  };

  const handleViewDashboard = (scanId) => {
    navigate(`/Dashboard/${scanId}`);
  };

  const handleViewAIHub = (scanId) => {
    navigate(`/ai-hub/${scanId}`);
  }

  const handleGenerateReport = () => {
    setShowGenerateModal(true);
  };

  const handleReportGenerated = (scanId) => {
    setShowGenerateModal(false);
    fetchAvailableScans();
    toast.success('Report generated successfully! It now appears in the table below.');
  };

  const handleDeleteRequest = (scanId, scanName) => {
    setDeleteConfirm({ show: true, scanId, scanName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.scanId) return;

    try {
      setDeleting(true);
      const response = await fetch(ApiNames.REPORTS_SCAN_DELETE(deleteConfirm.scanId), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      toast.success('Scan report deleted successfully!');
      await fetchAvailableScans();
      setDeleteConfirm({ show: false, scanId: null, scanName: null });
    } catch (error) {
      console.error('Error deleting scan:', error);
      toast.error('Failed to delete scan report. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, scanId: null, scanName: null });
  };

  const getStatusBadge = (status, complianceRate) => {
    const variants = {
      good: 'success',
      warning: 'warning',
      danger: 'danger'
    };

    const labels = {
      good: 'Excellent',
      warning: 'Needs Attention',
      danger: 'Critical'
    };

    return (
      <ModernBadge variant={variants[status] || 'warning'}>
        {labels[status] || 'Unknown'} - {complianceRate || '0'}%
      </ModernBadge>
    );
  };

  return (
    <div className="compliance-report-page">
      <div className="w-full">
        {/* Header */}
        <div className="compliance-report-header">
          <div className="header-content">
            <div className="header-title-row">
              <div className="header-icon-wrapper">
                <ShieldCheck className="header-icon" />
              </div>
              <div>
                <h1>Government Compliance Dashboard</h1>
                <p className="header-subtitle">
                  Comprehensive compliance monitoring across USWDS, OMB M-15-13, Section 508, and other federal standards
                </p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <ModernButton
              variant="secondary"
              icon={RefreshCw}
              size="md"
              onClick={handleRefresh}
              loading={refreshing}
            >
              Refresh
            </ModernButton>
            <ModernButton
              variant="primary"
              icon={FileCheck}
              size="md"
              onClick={handleGenerateReport}
            >
              Generate Report
            </ModernButton>
          </div>
        </div>

        {/* Stats Section */}
        <div className="stats-grid">
          <ModernStatCard
            title="Available Scans"
            value={availableScans.length.toLocaleString()}
            icon={CheckCircle}
            iconBgColor="bg-green-500"
          />
          <ModernStatCard
            title="Active Frameworks"
            value={systemStats.activeFrameworks.toLocaleString()}
            icon={ShieldCheck}
            iconBgColor="bg-blue-500"
          />
          <ModernStatCard
            title="Total Controls"
            value={systemStats.totalControls.toLocaleString()}
            icon={Server}
            iconBgColor="bg-purple-500"
          />
        </div>

        {/* Available Scans Section */}
        <ModernCard
          title="Generated Compliance Reports"
          subtitle="View detailed compliance dashboards for completed scans"
          icon={BarChart2}
        >
          {loading ? (
            <div className="loading-state">
              <div className="animate-pulse flex flex-col items-center py-12">
                <div className="w-12 h-12 bg-gray-200 rounded-xl mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          ) : availableScans.length === 0 ? (
            <div className="empty-state">
              <AlertTriangle className="empty-icon" />
              <h3 className="empty-title">No compliance reports generated</h3>
              <p className="empty-description">
                Generate your first compliance report to see it listed here.
              </p>
              <div className="empty-action">
                <ModernButton
                  variant="primary"
                  icon={FileCheck}
                  onClick={handleGenerateReport}
                >
                  Generate First Report
                </ModernButton>
              </div>
            </div>
          ) : (
            <div className="table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>URL Information</th>
                    <th>Scan Details</th>
                    <th>Compliance Status</th>
                    <th>Last Updated</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {availableScans.map(scan => (
                    <tr key={scan.scan_id}>
                      <td>
                        <div className="url-info">
                          <div className="url-name">{scan.url_name}</div>
                          {scan.url && (
                            <a
                              href={scan.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="url-link"
                            >
                              {scan.url}
                            </a>
                          )}
                          <div className="url-id">URL ID: {scan.url_id}</div>
                        </div>
                      </td>
                      <td>
                        <div className="scan-details">
                          <div className="scan-id">{scan.scan_id.substring(0, 12)}...</div>
                          <div className="scan-meta">
                            {scan.frameworks_count} frameworks • {scan.total_controls} controls
                          </div>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(scan.status, scan.compliance_rate)}
                      </td>
                      <td>
                        <div className="timestamp">
                          <Clock className="w-4 h-4 mr-2" />
                          {new Date(scan.generated_at).toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <ModernButton
                            variant="primary"
                            size="sm"
                            icon={Brain}
                            onClick={() => handleViewAIHub(scan.scan_id)}
                          >
                            View AI Dashboard
                          </ModernButton>
                          <ModernButton
                            variant="secondary"
                            size="sm"
                            icon={Eye}
                            onClick={() => handleViewDashboard(scan.scan_id)}
                          >
                            View Dashboard
                          </ModernButton>
                          <ModernButton
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            onClick={() => handleDeleteRequest(scan.scan_id, scan.url_name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Delete
                          </ModernButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModernCard>
      </div>

      {/* Generate Report Modal */}
      <GenerateReportModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onReportGenerated={handleReportGenerated}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-icon-wrapper">
              <Trash2 className="modal-icon" />
            </div>

            <h3 className="modal-title">Delete Scan Report</h3>

            <p className="modal-description">
              Are you sure you want to delete the scan report for{' '}
              <span className="font-medium text-gray-900">{deleteConfirm.scanName}</span>
              ? This action cannot be undone and will remove all associated data.
            </p>

            <div className="modal-actions">
              <ModernButton
                variant="secondary"
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </ModernButton>
              <ModernButton
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                loading={deleting}
                className="flex-1"
              >
                Delete
              </ModernButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceReport;
